/**
 * FitText — scales its single-line content DOWN so it always fits the available
 * width. It never wraps, never truncates, and never overflows its box.
 *
 * The content renders at its natural font size; a ResizeObserver then measures the
 * box vs. the content and applies a horizontal `transform: scale()` (origin left)
 * only when the content is wider than the box. Transform doesn't trigger reflow, so
 * shrinking never shifts siblings — CLS-safe. Measuring reads the UNTRANSFORMED
 * layout width (`scrollWidth`), so the scale converges in one pass and never
 * oscillates.
 *
 * Built for ledger figures — currency balances that scale into the billions at the
 * product's target portfolio size — where a fixed font would eventually break the
 * card and an ellipsis would silently misstate the number.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

// Run before paint on the client (no visible full-size flash), but fall back to a
// passive effect on the server so SSR doesn't warn about useLayoutEffect.
const useIsomorphicLayoutEffect =
  typeof document !== 'undefined' ? useLayoutEffect : useEffect;

export interface FitTextProps {
  readonly children: ReactNode;
  /** Floor for the scale factor, so an extreme value stays legible rather than
   *  shrinking to nothing (default 0.4). */
  readonly minScale?: number;
  /** Class applied to the outer (measured) box — carries the font styling. Accepts
   *  `string | undefined` so a CSS-module member (typed so under noUncheckedIndexedAccess)
   *  can pass through under exactOptionalPropertyTypes. */
  readonly className?: string | undefined;
}

export function FitText({ children, minScale = 0.4, className }: FitTextProps): ReactNode {
  const boxRef = useRef<HTMLSpanElement>(null);
  const innerRef = useRef<HTMLSpanElement>(null);
  const [scale, setScale] = useState(1);

  useIsomorphicLayoutEffect(() => {
    const box = boxRef.current;
    const inner = innerRef.current;
    if (box === null || inner === null) return;
    const measure = (): void => {
      const available = box.clientWidth;
      const needed = inner.scrollWidth;
      if (available <= 0 || needed <= 0) return;
      setScale(needed > available ? Math.max(minScale, available / needed) : 1);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, [children, minScale]);

  return (
    <span ref={boxRef} className={className} style={{ display: 'block', minWidth: 0, overflow: 'hidden' }}>
      <span
        ref={innerRef}
        style={{
          display: 'inline-block',
          whiteSpace: 'nowrap',
          transform: scale < 1 ? `scale(${scale})` : undefined,
          transformOrigin: 'left center',
        }}
      >
        {children}
      </span>
    </span>
  );
}
